import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SessionsClient } from '@unity/clients';
import { RouteService } from '@unity/services/route.service';
import { SessionService } from '@unity/services/session.service';
import { ToasterService } from '@unity/services/toaster.service';
import { catchError, EMPTY, Observable, switchMap, tap, throwError } from 'rxjs';

export const NO_REFRESH = new HttpContextToken(() => false);

@Injectable({
  providedIn: 'root',
})
export class AuthRefreshInterceptor implements HttpInterceptor {
  private readonly routeService = inject(RouteService);

  private readonly sessionsClient = inject(SessionsClient);

  private readonly sessionService = inject(SessionService);
  private readonly toasterService = inject(ToasterService);

  private sendToLogin = (e: HttpErrorResponse) => {
    console.error(e);
    this.sessionService.logout({
      returnUrl: this.routeService.getActiveUnityUrl(),
      queryParams: { status: 'Expired' },
    });
    this.toasterService.showError('Login Status', e, 'warning');
    return EMPTY;
  };

  private isExpiredError(e: HttpErrorResponse): boolean {
    const isExpired = this.sessionService.$isExpired();
    return isExpired && e.status === HttpStatusCode.Unauthorized;
  }

  private cloneRequest(original: HttpRequest<unknown>, newToken: string) {
    return original.clone({
      setHeaders: {
        Authorization: `Bearer ${newToken}`,
      },
    });
  }

  private refresh(request: HttpRequest<unknown>, next: HttpHandler) {
    return this.sessionsClient.refreshSession(new HttpContext().set(NO_REFRESH, true)).pipe(
      tap(res => this.sessionService.$token.set(res.access_token)),
      catchError(e => this.sendToLogin(e)),
      switchMap(refreshed => {
        const retry = this.cloneRequest(request, refreshed.access_token);
        return next.handle(retry);
      })
    );
  }

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.context.get(NO_REFRESH)) {
      // If this request IS a refresh, don't recurse
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((requestError: HttpErrorResponse) => {
        if (this.isExpiredError(requestError)) {
          // eslint-disable-next-line @unity-rules/warn-console-log
          console.warn('Access token expired, refreshing...');
          return this.refresh(request, next);
        } else {
          return throwError(() => requestError);
        }
      })
    );
  }
}
